/* eslint-disable react/no-unknown-property */
/* eslint-disable @typescript-eslint/no-explicit-any */
import React from 'react';
import { OrbitControls } from '@react-three/drei';
import { useThree, extend } from '@react-three/fiber';
import * as THREE from 'three';
import { SafeDataObject } from '@preview/helpers/SafeDataObject';
import type { MockItem } from '../../types';
import { ThreeJSCard } from '../ThreeJSCard';
import { ThreeJSConnection } from '../ThreeJSConnection';

// Extend Three.js elements for JSX
extend({
  PointLight: THREE.PointLight,
  AmbientLight: THREE.AmbientLight,
  SpotLight: THREE.SpotLight,
  DirectionalLight: THREE.DirectionalLight,
});

// Make TypeScript aware of the JSX elements we're extending
declare global {
  namespace JSX {
    interface IntrinsicElements {
      ambientLight: React.DetailedHTMLProps<React.HTMLAttributes<HTMLElement>, HTMLElement> & {
        intensity?: number;
      };
      directionalLight: React.DetailedHTMLProps<React.HTMLAttributes<HTMLElement>, HTMLElement>;
      pointLight: React.DetailedHTMLProps<React.HTMLAttributes<HTMLElement>, HTMLElement> & {
        intensity?: number;
        position?: [number, number, number];
      };
      spotLight: React.DetailedHTMLProps<React.HTMLAttributes<HTMLElement>, HTMLElement>;
    }
  }
}

// Calculate positions for cards based on their relationships
const calculateCardPositions = (
  mocks: Record<string, MockItem>,
  _relationships: Record<string, string[]>
): Record<string, [number, number, number]> => {
  const positions: Record<string, [number, number, number]> = {};
  const mockIds = Object.keys(mocks);

  // Skip the site token mock
  const visibleMockIds = mockIds.filter((id) => id !== 'mock_site_token');

  // First, identify the root nodes (feed API)
  const rootNodes = visibleMockIds.filter(
    (id) => mocks[id].endpoint.includes('sites') || mocks[id].endpoint.includes('feed')
  );

  // Position root nodes at the top - center the feeds call if there's only one
  if (rootNodes.length === 1) {
    positions[rootNodes[0]] = [0, 2, 0]; // Centered position
  } else {
    rootNodes.forEach((id, index) => {
      positions[id] = [index * 2 - (rootNodes.length - 1), 2, 0];
    });
  }

  // Position playlist nodes in the middle
  const playlistNodes = visibleMockIds.filter(
    (id) => mocks[id].endpoint.includes('playlists') && !rootNodes.includes(id)
  );

  playlistNodes.forEach((id, index) => {
    positions[id] = [index * 2 - (playlistNodes.length - 1), 0, 0];
  });

  // Position video nodes at the bottom
  const videoNodes = visibleMockIds.filter(
    (id) => mocks[id].endpoint.includes('videos') && !rootNodes.includes(id) && !playlistNodes.includes(id)
  );

  videoNodes.forEach((id, index) => {
    positions[id] = [index * 2 - (videoNodes.length - 1), -2, 0];
  });

  // Position any remaining nodes
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  const remainingNodes = visibleMockIds.filter((id) => !positions[id]);

  remainingNodes.forEach((id, index) => {
    positions[id] = [index * 2 - (remainingNodes.length - 1), -4, 0];
  });

  return positions;
};

// Find relationships between mocks
const findRelationships = (mocks: Record<string, MockItem>): Record<string, string[]> => {
  const relationships: Record<string, string[]> = {};

  // Initialize empty arrays for each mock
  Object.keys(mocks).forEach((id) => {
    relationships[id] = [];
  });

  // Find feed -> playlist relationships
  Object.entries(mocks).forEach(([id, mock]) => {
    if (mock.endpoint.includes('sites') || mock.endpoint.includes('feed')) {
      const data = new SafeDataObject(mock.data).toJSON();
      if (data.feeds) {
        data.feeds.forEach((feed: any) => {
          if (feed.playlists) {
            feed.playlists.forEach((playlist: any) => {
              // Find playlist mocks that match this ID
              Object.entries(mocks).forEach(([playlistId, playlistMock]) => {
                if (
                  playlistMock.endpoint.includes('playlists') &&
                  (playlistMock.endpoint.includes(`/${playlist.id}`) || playlistMock.endpoint.includes(`/${playlist}`))
                ) {
                  relationships[id].push(playlistId);
                }
              });
            });
          }
        });
      }
    }
  });

  // Find playlist -> video relationships
  Object.entries(mocks).forEach(([id, mock]) => {
    if (mock.endpoint.includes('playlists')) {
      const data = new SafeDataObject(mock.data).toJSON();
      if (data.videos) {
        data.videos.forEach((video: any) => {
          // Find video mocks that match this ID
          Object.entries(mocks).forEach(([videoId, videoMock]) => {
            if (videoMock.endpoint.includes('videos') && videoMock.endpoint.includes(`/${video.id}`)) {
              relationships[id].push(videoId);
            }
          });
        });
      }
    }
  });

  return relationships;
};

// Define colors for different types of objects
const typeColors = {
  site: '#4CAF50', // Green for sites/feeds
  playlist: '#2196F3', // Blue for playlists
  video: '#FF9800', // Orange for videos
  default: '#9E9E9E', // Grey for any other type
};

// Get color based on mock type
const getMockTypeColor = (mock: MockItem): string => {
  if (mock.endpoint.includes('sites') || mock.endpoint.includes('feed')) {
    return typeColors.site;
  } else if (mock.endpoint.includes('playlists')) {
    return typeColors.playlist;
  } else if (mock.endpoint.includes('videos')) {
    return typeColors.video;
  }
  return typeColors.default;
};

type _Props = {
  activeMocks: string[];
  mocks: Record<string, MockItem>;
  onSelectMock: (id: string) => void;
};

const ThreeJSSceneContent = ({ activeMocks, mocks, onSelectMock }: _Props) => {
  const { camera } = useThree();

  // Find relationships between mocks
  const relationships = findRelationships(mocks);

  // Calculate positions for each card
  const positions = calculateCardPositions(mocks, relationships);

  // Set initial camera position
  React.useEffect(() => {
    if (camera instanceof THREE.PerspectiveCamera) {
      camera.position.set(0, 0, 6);
      camera.lookAt(0, 0, 0);
    }
  }, [camera]);

  return (
    <>
      <ambientLight intensity={0.5} />
      <pointLight intensity={0.8} position={[10, 10, 10]} />

      {/* Render cards */}
      {Object.entries(mocks).map(([id, mock]) => {
        // Skip rendering the site token
        if (id === 'mock_site_token') return null;

        return (
          <ThreeJSCard
            key={id}
            color={activeMocks.includes(id) ? getMockTypeColor(mock) : '#d9d9d9'}
            onClick={() => onSelectMock(id)}
            // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
            position={positions[id] || [0, 0, 0]}
            title={mock.description || mock.endpoint}
          />
        );
      })}

      {/* Render connections */}
      {Object.entries(relationships).map(([sourceId, targetIds]) =>
        targetIds.map((targetId, index) => {
          // Get positions
          // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
          const sourcePos = positions[sourceId] || [0, 0, 0];
          // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
          const targetPos = positions[targetId] || [0, 0, 0];

          // Calculate bottom center of source card (using height=1 from Card3D)
          const sourceBottom: [number, number, number] = [sourcePos[0], sourcePos[1] - 0.5, sourcePos[2]];

          // Calculate top center of target card
          const targetTop: [number, number, number] = [targetPos[0], targetPos[1] + 0.5, targetPos[2]];

          // Determine connection color based on active state
          const connectionColor =
            activeMocks.includes(sourceId) && activeMocks.includes(targetId) ? '#1890ff' : '#d9d9d9';

          // Extract relationship value
          let relationshipValue = '';
          const sourceMock = new SafeDataObject(mocks[sourceId]).toJSON();
          const targetMock = new SafeDataObject(mocks[targetId]).toJSON();

          // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
          if (sourceMock && targetMock) {
            // Determine the type of relationship
            if (sourceMock.endpoint.includes('sites') || sourceMock.endpoint.includes('feed')) {
              // Feed → Playlist relationship
              if (targetMock.endpoint.includes('playlists')) {
                const targetId = targetMock.endpoint.split('/').pop() || '';
                relationshipValue = `Feed → Playlist: ${targetId}`;

                // Add title if available
                if (targetMock.data?.title) {
                  relationshipValue += `\n"${targetMock.data.title}"`;
                }
              }
            } else if (sourceMock.endpoint.includes('playlists')) {
              // Playlist → Video relationship
              if (targetMock.endpoint.includes('videos')) {
                const targetId = targetMock.endpoint.split('/').pop() || '';
                relationshipValue = `Playlist → Video: ${targetId}`;

                // Add title if available
                if (targetMock.data?.title) {
                  relationshipValue += `\n"${targetMock.data.title}"`;
                }
              }
            }
          }

          return (
            <ThreeJSConnection
              key={`${sourceId}-${targetId}-${index}`}
              color={connectionColor}
              end={targetTop}
              relationshipValue={relationshipValue}
              start={sourceBottom}
            />
          );
        })
      )}

      <OrbitControls
        enablePan={true}
        enableRotate={true}
        enableZoom={true}
        maxAzimuthAngle={Math.PI / 4} // 45 degrees
        maxDistance={10}
        maxPolarAngle={Math.PI / 2} // 90 degrees (horizontal view)
        minAzimuthAngle={-Math.PI / 4} // -45 degrees
        minDistance={2}
        minPolarAngle={Math.PI / 4} // 45 degrees from top
        rotateSpeed={0.5}
      />
    </>
  );
};

export default ThreeJSSceneContent;
